import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  ChoiceList,
  DataTable,
  Filters,
  InlineStack,
  Modal,
  Pagination,
  Select,
  Spinner,
  Text,
  TextField
} from '@shopify/polaris';
import { paymentReconciliationService } from '../services/paymentReconciliationService';
import { accountingHelpers } from '../constants/accountingConstants';

const getCustomerName = (order) => {
  if (order.customer?.name) return order.customer.name;
  return [order.customer?.first_name, order.customer?.last_name].filter(Boolean).join(' ') || 'Unknown';
};

const currentLocalDateTime = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};

const emptyManualPayment = () => ({
  transaction_id: '',
  payer_name: '',
  payer_account: '',
  amount: '',
  currency: 'EGP',
  paid_at: currentLocalDateTime()
});

export const doesPaymentMatchOrder = (order, payment) => Boolean(
  order
  && payment
  && (
    order.expected_amount === null
    || order.expected_amount === undefined
    || Math.abs(order.expected_amount - payment.amount) < 0.01
  )
  && String(order.payment?.currency || payment.currency || 'EGP').toUpperCase()
    === String(payment.currency || 'EGP').toUpperCase()
);

const PaymentsReconciliationPage = () => {
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [status, setStatus] = useState(['unclaimed']);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [orders, setOrders] = useState([]);
  const [orderSearch, setOrderSearch] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [manualPaymentOpen, setManualPaymentOpen] = useState(false);
  const [manualPayment, setManualPayment] = useState(emptyManualPayment);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualError, setManualError] = useState('');

  const loadPayments = useCallback(async (page = 1, overrides = {}) => {
    setLoading(true);
    setError('');
    try {
      const response = await paymentReconciliationService.getPayments({
        page,
        limit: 20,
        status: overrides.status !== undefined ? overrides.status : (status[0] || ''),
        search: overrides.search !== undefined ? overrides.search : search
      });
      setPayments(response.payments || []);
      setPagination(response.pagination || { page, totalPages: 1, total: 0 });
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    loadPayments(1);
  }, [loadPayments]);

  const openManualPayment = useCallback(() => {
    setManualPayment(emptyManualPayment());
    setError('');
    setSuccess('');
    setManualError('');
    setManualPaymentOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener('accountingInstapayCreate', openManualPayment);
    return () => window.removeEventListener('accountingInstapayCreate', openManualPayment);
  }, [openManualPayment]);

  const loadOrders = useCallback(async (query = '') => {
    setOrdersLoading(true);
    setError('');
    try {
      const response = await paymentReconciliationService.getUnpaidOrders(query);
      const nextOrders = response.data || [];
      setOrders(nextOrders);
      setSelectedOrderId((current) => (
        nextOrders.some((order) => order.order_id === current)
          ? current
          : ''
      ));
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  const openClaimModal = async (payment) => {
    setSelectedPayment(payment);
    setOrderSearch('');
    setOrders([]);
    setSelectedOrderId('');
    await loadOrders('');
  };

  const closeClaimModal = () => {
    if (claiming) return;
    setSelectedPayment(null);
    setOrders([]);
    setSelectedOrderId('');
  };

  const handleClaim = async () => {
    if (!selectedPayment || !selectedOrderId) return;
    setClaiming(true);
    setError('');
    try {
      await paymentReconciliationService.claimPayment(selectedPayment.payment_id, selectedOrderId);
      setSuccess(`Payment linked to order ${selectedOrderId}`);
      setSelectedPayment(null);
      setOrders([]);
      setSelectedOrderId('');
      await loadPayments(pagination.page);
    } catch (claimError) {
      setError(claimError.message);
    } finally {
      setClaiming(false);
    }
  };

  const handleManualPayment = async () => {
    setManualSubmitting(true);
    setManualError('');
    try {
      const paidAt = new Date(manualPayment.paid_at);
      if (!Number.isFinite(paidAt.getTime())) {
        throw new Error('Paid at must be a valid date and time');
      }
      await paymentReconciliationService.createManualPayment({
        ...manualPayment,
        amount: Number(manualPayment.amount),
        paid_at: paidAt.toISOString()
      });
      setManualPaymentOpen(false);
      setSuccess('InstaPay payment recorded. You can now link it to an eligible order.');
      const filtersAlreadyShowNewPayment = status[0] === 'unclaimed' && search === '';
      setStatus(['unclaimed']);
      setSearch('');
      if (filtersAlreadyShowNewPayment) {
        await loadPayments(1);
      }
    } catch (createError) {
      setManualError(createError.message);
    } finally {
      setManualSubmitting(false);
    }
  };

  const selectedOrder = orders.find((order) => order.order_id === selectedOrderId);
  const selectedOrderMatches = doesPaymentMatchOrder(selectedOrder, selectedPayment);
  const orderOptions = useMemo(() => [
    { label: ordersLoading ? 'Loading eligible orders...' : 'Select an eligible order', value: '' },
    ...orders.map((order) => ({
      label: `${order.order_id} · ${getCustomerName(order)} · ${accountingHelpers.formatCurrency(order.expected_amount, order.payment?.currency || 'EGP')}`,
      value: order.order_id
    }))
  ], [orders, ordersLoading]);

  const filters = [{
    key: 'status',
    label: 'Claim status',
    filter: (
      <ChoiceList
        title="Claim status"
        titleHidden
        choices={[
          { label: 'Unclaimed', value: 'unclaimed' },
          { label: 'Claimed', value: 'claimed' }
        ]}
        selected={status}
        onChange={setStatus}
        allowMultiple={false}
      />
    ),
    shortcut: true
  }];

  const appliedFilters = status.length > 0 ? [{
    key: 'status',
    label: status[0] === 'claimed' ? 'Claimed' : 'Unclaimed',
    onRemove: () => setStatus([])
  }] : [];

  const rows = payments.map((payment) => [
    payment.paid_at ? new Date(payment.paid_at).toLocaleString() : '-',
    payment.payer_name || 'Unknown',
    accountingHelpers.formatCurrency(payment.amount, payment.currency || 'EGP'),
    payment.external_transaction_id || '-',
    payment.status === 'claimed'
      ? <Badge tone="success">Claimed</Badge>
      : <Badge tone="attention">Unclaimed</Badge>,
    payment.claimed_order_id || '-',
    payment.status === 'unclaimed'
      ? <Button size="slim" onClick={() => openClaimModal(payment)}>Link order</Button>
      : null
  ]);

  return (
    <>
      <BlockStack gap="400">
        <Text as="p" tone="subdued">
          {pagination.total || 0} payment{pagination.total === 1 ? '' : 's'}
        </Text>
        {error && <Banner tone="critical" onDismiss={() => setError('')}>{error}</Banner>}
        {success && <Banner tone="success" onDismiss={() => setSuccess('')}>{success}</Banner>}

        <Card>
          <BlockStack gap="300">
            <Filters
              queryValue={search}
              queryPlaceholder="Search payer, transaction, or order"
              onQueryChange={setSearch}
              onQueryClear={() => setSearch('')}
              filters={filters}
              appliedFilters={appliedFilters}
              onClearAll={() => {
                setSearch('');
                setStatus([]);
              }}
            />

            {loading ? (
              <InlineStack align="center"><Spinner accessibilityLabel="Loading payments" /></InlineStack>
            ) : payments.length === 0 ? (
              <Text as="p" tone="subdued">No payments match the selected filters.</Text>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'numeric', 'text', 'text', 'text', 'text']}
                headings={['Paid at', 'Payer', 'Amount', 'Transaction', 'Status', 'Order', 'Action']}
                rows={rows}
              />
            )}

            <InlineStack align="center">
              <Pagination
                hasPrevious={pagination.page > 1}
                onPrevious={() => loadPayments(pagination.page - 1)}
                hasNext={pagination.page < pagination.totalPages}
                onNext={() => loadPayments(pagination.page + 1)}
                label={`Page ${pagination.page} of ${Math.max(pagination.totalPages || 1, 1)}`}
              />
            </InlineStack>
          </BlockStack>
        </Card>
      </BlockStack>

      <Modal
        open={manualPaymentOpen}
        onClose={() => !manualSubmitting && setManualPaymentOpen(false)}
        title="Record InstaPay payment"
        primaryAction={{
          content: 'Record payment',
          onAction: handleManualPayment,
          loading: manualSubmitting,
          disabled: !manualPayment.transaction_id.trim()
            || !manualPayment.payer_name.trim()
            || Number(manualPayment.amount) <= 0
            || !manualPayment.paid_at
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => setManualPaymentOpen(false),
          disabled: manualSubmitting
        }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {manualError && (
              <Banner tone="critical" onDismiss={() => setManualError('')}>
                {manualError}
              </Banner>
            )}
            <Banner>
              Record the bank transfer first, then use “Link order” to match it to an eligible order.
            </Banner>
            <TextField
              label="Transaction ID"
              value={manualPayment.transaction_id}
              onChange={(value) => setManualPayment(current => ({
                ...current,
                transaction_id: value
              }))}
              autoComplete="off"
            />
            <TextField
              label="Payer name"
              value={manualPayment.payer_name}
              onChange={(value) => setManualPayment(current => ({
                ...current,
                payer_name: value
              }))}
              autoComplete="off"
            />
            <TextField
              label="Payer account or phone"
              value={manualPayment.payer_account}
              onChange={(value) => setManualPayment(current => ({
                ...current,
                payer_account: value
              }))}
              autoComplete="off"
            />
            <InlineStack gap="300">
              <TextField
                label="Amount"
                type="number"
                min={0.01}
                step={0.01}
                value={manualPayment.amount}
                onChange={(value) => setManualPayment(current => ({
                  ...current,
                  amount: value
                }))}
                suffix="EGP"
                autoComplete="off"
              />
              <TextField
                label="Paid at"
                type="datetime-local"
                value={manualPayment.paid_at}
                onChange={(value) => setManualPayment(current => ({
                  ...current,
                  paid_at: value
                }))}
                autoComplete="off"
              />
            </InlineStack>
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={Boolean(selectedPayment)}
        onClose={closeClaimModal}
        title="Link InstaPay payment to order"
        primaryAction={{
          content: 'Link payment',
          onAction: handleClaim,
          loading: claiming,
          disabled: !selectedOrderId || !selectedOrderMatches
        }}
        secondaryActions={[{ content: 'Cancel', onAction: closeClaimModal, disabled: claiming }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {selectedPayment && (
              <Banner>
                {selectedPayment.payer_name} paid {accountingHelpers.formatCurrency(
                  selectedPayment.amount,
                  selectedPayment.currency || 'EGP'
                )}.
              </Banner>
            )}
            <InlineStack gap="200" blockAlign="end">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Find an eligible order"
                  labelHidden
                  placeholder="Search order ID, customer, phone, or email"
                  value={orderSearch}
                  onChange={setOrderSearch}
                  clearButton
                  onClearButtonClick={() => setOrderSearch('')}
                  autoComplete="off"
                />
              </div>
              <Button onClick={() => loadOrders(orderSearch)} loading={ordersLoading}>Search orders</Button>
            </InlineStack>
            <Select
              label="Eligible order"
              options={orderOptions}
              value={selectedOrderId}
              onChange={setSelectedOrderId}
              disabled={ordersLoading}
            />
            {selectedOrder && selectedPayment && selectedOrder.expected_amount !== null && (
              <Text
                as="p"
                tone={selectedOrderMatches ? 'success' : 'critical'}
              >
                Order amount: {accountingHelpers.formatCurrency(
                  selectedOrder.expected_amount,
                  selectedOrder.payment?.currency || selectedPayment.currency || 'EGP'
                )}{selectedOrderMatches ? ' · Matches payment' : ' · Must match payment amount and currency'}
              </Text>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
};

export default PaymentsReconciliationPage;
